import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Archive, Download, FileText, Loader2, Trash2 } from 'lucide-react';
import { Input } from '../ui/input';
import { ouvrirPdf } from '../reglement-interieur/SignerReglementDialog';

// Copies figées (PDF + PowerPoint) de la présentation, une par session, pour comparer d'une année sur l'autre
export const ArchivesPresentation = ({ api, edition, libelleDefaut, avant }) => {
  const [liste, setListe] = useState([]);
  const [libelle, setLibelle] = useState('');
  const [charge, setCharge] = useState(false);
  const charger = useCallback(() => axios.get(`${api}/archives`).then(({ data }) => setListe(data)).catch(() => setListe([])), [api]);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { setLibelle(libelleDefaut || ''); }, [libelleDefaut]);

  const archiver = async () => {
    setCharge(true);
    try {
      await avant();
      setListe((await axios.post(`${api}/archives`, { libelle }, { timeout: 180000 })).data);
      toast.success('Version archivée');
    } catch (e) { toast.error(e.response?.data?.detail || 'Archivage impossible'); }
    finally { setCharge(false); }
  };
  const supprimer = async (a) => {
    if (!window.confirm(`Supprimer l'archive « ${a.libelle} » ?`)) return;
    try { setListe((await axios.delete(`${api}/archives/${a.archive_id}`)).data); } catch { toast.error('Suppression impossible'); }
  };
  const pptx = async (a) => {
    let data;
    try { ({ data } = await axios.get(`${api}/archives/${a.archive_id}/pptx`, { responseType: 'blob' })); } catch { toast.error('Téléchargement impossible'); return; }
    const lien = document.createElement('a');
    lien.href = URL.createObjectURL(data);
    lien.download = `${a.libelle}.pptx`;
    lien.click();
    URL.revokeObjectURL(lien.href);
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid="presentation-archives">
      <h3 className="flex items-center gap-2 font-semibold text-[#0F172A]"><Archive className="h-4 w-4 text-[#8DC63F]" /> Archives par session</h3>
      <p className="mt-1 text-xs text-slate-500">Une copie figée en PDF et PowerPoint, qui ne bouge plus même si vous modifiez la présentation ensuite.</p>
      {edition && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Input value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder="Ex. Session 2026" className="max-w-xs text-sm" data-testid="archive-libelle" />
          <button type="button" onClick={archiver} disabled={charge || !libelle.trim()} data-testid="archive-creer"
            className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#1E293B] disabled:opacity-50">
            {charge ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />} Archiver cette version
          </button>
        </div>
      )}
      <ul className="mt-3 divide-y divide-slate-100">
        {!liste.length && <li className="py-2 text-sm text-slate-400">Aucune archive pour l'instant.</li>}
        {liste.map((a) => (
          <li key={a.archive_id} className="flex flex-wrap items-center gap-3 py-2" data-testid={`archive-${a.archive_id}`}>
            <span className="flex-1 text-sm"><strong>{a.libelle}</strong>
              <span className="ml-2 text-xs text-slate-500">{new Date(a.date).toLocaleDateString('fr-FR')} · {a.diapos} diapos{a.par ? ` · ${a.par}` : ''}</span></span>
            <button type="button" onClick={() => ouvrirPdf(`${api}/archives/${a.archive_id}/pdf`, `${a.libelle}.pdf`)} data-testid={`archive-pdf-${a.archive_id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-[#0F172A]"><FileText className="h-3.5 w-3.5" /> PDF</button>
            <button type="button" onClick={() => pptx(a)} data-testid={`archive-pptx-${a.archive_id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-[#0F172A]"><Download className="h-3.5 w-3.5" /> PowerPoint</button>
            {edition && <button type="button" onClick={() => supprimer(a)} aria-label="Supprimer" data-testid={`archive-supprimer-${a.archive_id}`}
              className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
          </li>
        ))}
      </ul>
    </section>
  );
};
